import { isApiError } from "@pokifolio/api-client";
import {
  type CardLink,
  type CardLinkInput,
  LINK_STATUSES,
  type LinkStatus,
  type MeResponse,
  RAW_CONDITIONS,
  type RawCondition,
  hasPermission,
} from "@pokifolio/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Check, ExternalLink, Pencil, Plus, Send, Trash2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { type SubmitEvent, useEffect, useId, useState } from "react";
import { toast } from "sonner";

import { useApi } from "../../app/api-context";
import { ConfirmDialog } from "../../components/confirm-dialog";
import { ErrorAlert } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardTitle } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Field } from "../../components/ui/field";
import { Input, Select, Textarea } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Skeleton } from "../../components/ui/skeleton";
import { describeError, serverFieldError } from "../../lib/errors";
import { useCurrentUser } from "../auth/session";
import { RAW_CONDITION_LABELS, formatDate } from "../research/format";
import { formatMoney } from "../search/format";
import {
  LINK_STATUS_LABELS,
  LINK_STATUS_TONES,
  describeCardStep,
  describeLinkCondition,
  describeLinkPrice,
  describeSource,
  whatsAppAskLink,
} from "./card-format";
import { CardPicture } from "./card-picture";
import { DealControls, PriorityBadge } from "./deal-controls";
import { CARDS_KEY, useCard, useCardChange } from "./card-queries";
import { LinkForm } from "./link-form";

const backLink = (
  <Link to="/cards" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline">
    <ArrowLeft className="size-4" aria-hidden="true" />
    All cards
  </Link>
);

const mayChange = (me: MeResponse, ownerId: string) =>
  me.user.id === ownerId ||
  hasPermission(me.permissions, "research.update_any") ||
  hasPermission(me.permissions, "research.approve");

/** One card: add its listing links, and send each one to the admin (D-64). */
export function CardPage({ cardId }: { cardId: string }) {
  const api = useApi();
  const navigate = useNavigate();
  const me = useCurrentUser();
  const card = useCard(cardId);
  const addLink = useCardChange(cardId, (values: Parameters<typeof api.addCardLink>[1]) =>
    api.addCardLink(cardId, values),
  );
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [statusFilter, setStatusFilter] = useState<LinkStatus | "all">("all");
  const [conditionFilter, setConditionFilter] = useState<RawCondition | "all">("all");
  const [showClosed, setShowClosed] = useState(false);
  const isAdmin = hasPermission(me.permissions, "research.approve");
  const queryClient = useQueryClient();
  // The admin opening a card marks its new listings "Opened" (D-67).
  useEffect(() => {
    if (!isAdmin) return;
    void api.markCardViewed(cardId).then(async () => {
      await queryClient.refetchQueries({ queryKey: [...CARDS_KEY, "card", cardId] });
      await queryClient.invalidateQueries({ queryKey: CARDS_KEY });
    });
  }, [api, cardId, isAdmin, queryClient]);

  if (card.isPending)
    return (
      <div className="flex flex-col gap-4">
        {backLink}
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (card.isError) {
    const missing = isApiError(card.error) && (card.error.status === 404 || card.error.status === 422);
    return (
      <div className="flex flex-col gap-4">
        {backLink}
        {missing ? (
          <Card>
            <h1 className="text-lg font-semibold text-foreground">This card was not found.</h1>
          </Card>
        ) : (
          <ErrorAlert error={card.error} />
        )}
      </div>
    );
  }

  const { card: info, links } = card.data;
  const closedCount = links.filter((l) => l.dealStatus === "closed").length;
  const shown = links.filter(
    (l) =>
      (showClosed || l.dealStatus !== "closed") &&
      (statusFilter === "all" || l.status === statusFilter) &&
      (conditionFilter === "all" || l.condition === conditionFilter),
  );
  return (
    <div className="flex flex-col gap-6">
      {backLink}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-4">
          <CardPicture
            cardId={cardId}
            name={info.name}
            version={info.imageVersion}
            canChange={mayChange(me, info.addedBy.id)}
          />
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              {info.name}
              {info.closed && <Badge tone="neutral">Closed</Badge>}
            </h1>
            {info.setName !== null && <p className="text-sm text-foreground">{info.setName}</p>}
            <p className="mt-1 text-sm text-muted-foreground">
              Added by {info.addedBy.id === me.user.id ? "you" : info.addedBy.name} on{" "}
              {formatDate(info.addedAt)} · {info.linkCount} {info.linkCount === 1 ? "listing" : "listings"} ·{" "}
              {describeCardStep(info)}
            </p>
          </div>
        </div>
        {mayChange(me, info.addedBy.id) && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                void api
                  .setCardClosed(cardId, { closed: !info.closed })
                  .then(async (r) => {
                    queryClient.setQueryData([...CARDS_KEY, "card", cardId], r);
                    await queryClient.invalidateQueries({ queryKey: CARDS_KEY });
                    toast.success(
                      info.closed ? "Card opened again." : "Card closed. Its listings are hidden.",
                    );
                  })
                  .catch((e: unknown) => toast.error(describeError(e).message))
              }
            >
              {info.closed ? "Open card again" : "Close card"}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setDeleting(true)}>
              <Trash2 aria-hidden="true" />
              Delete card…
            </Button>
          </div>
        )}
      </div>

      {adding || links.length === 0 ? (
        <Card className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Add a listing</CardTitle>
            {links.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
                Close
              </Button>
            )}
          </div>
          <LinkForm
            onSave={async (values) => {
              await addLink.mutateAsync(values);
              // Stays open for the next listing until Close.
              setAdding(true);
              toast.success("Listing saved.");
            }}
          />
        </Card>
      ) : null}

      <section aria-labelledby="links-heading" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="links-heading" className="text-lg font-semibold text-foreground">
            Listings (
            {shown.length === links.length
              ? links.length
              : `${String(shown.length)} of ${String(links.length)}`}
            )
          </h2>
          {!adding && links.length > 0 && (
            <Button size="sm" onClick={() => setAdding(true)}>
              <Plus aria-hidden="true" />
              Add listing
            </Button>
          )}
        </div>
        {links.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter listings">
            {(["all", ...LINK_STATUSES] as const).map((s) => {
              const n = s === "all" ? links.length : links.filter((l) => l.status === s).length;
              return (
                <Button
                  key={s}
                  size="sm"
                  variant={statusFilter === s ? "primary" : "secondary"}
                  aria-pressed={statusFilter === s}
                  onClick={() => setStatusFilter(s)}
                >
                  {s === "all" ? "All" : LINK_STATUS_LABELS[s]} ({n})
                </Button>
              );
            })}
            <Select
              aria-label="Condition"
              value={conditionFilter}
              onChange={(e) => setConditionFilter(e.target.value as RawCondition | "all")}
              className="h-8 w-44"
            >
              <option value="all">All conditions</option>
              {RAW_CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {RAW_CONDITION_LABELS[c]}
                </option>
              ))}
            </Select>
            {closedCount > 0 && (
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={showClosed}
                  onChange={(e) => setShowClosed(e.target.checked)}
                  className="size-4 accent-primary"
                />
                Show closed ({closedCount})
              </label>
            )}
          </div>
        )}
        {links.length === 0 ? (
          <Card className="py-8 text-center">
            <p className="text-sm text-muted-foreground">No listings yet. Add the first one above.</p>
          </Card>
        ) : shown.length === 0 ? (
          <Card className="py-8 text-center">
            <p className="text-sm text-muted-foreground">No listings match this filter.</p>
          </Card>
        ) : (
          <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <Table className="min-w-[900px]">
              <caption className="sr-only">Listings</caption>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {["Listing", "Price", "Condition", "Approval", "Status", "Added by", ""].map((h, n) => (
                    <TableHead
                      key={n}
                      scope="col"
                      className="text-xs uppercase tracking-wide text-muted-foreground"
                    >
                      {h === "" ? <span className="sr-only">Actions</span> : h}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((link) => (
                  <LinkItem key={link.id} cardId={cardId} cardName={info.name} link={link} me={me} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={`Delete ${info.name}?`}
        description="The card and its listings leave the list. They stay in the activity log."
        confirmText="Delete"
        destructive
        onConfirm={() =>
          void api
            .deleteCard(cardId)
            .then(async () => {
              toast.success(`${info.name} deleted.`);
              await navigate({ to: "/cards" });
            })
            .catch((e: unknown) => toast.error(describeError(e).message))
        }
      />
    </div>
  );
}

function LinkItem({
  cardId,
  cardName,
  link,
  me,
}: {
  cardId: string;
  cardName: string;
  link: CardLink;
  me: MeResponse;
}) {
  const api = useApi();
  const [editing, setEditing] = useState(false);
  const [deciding, setDeciding] = useState<"approve" | "decline" | null>(null);
  const [removing, setRemoving] = useState(false);
  const change = useCardChange(cardId, (step: "ask" | "delete") =>
    step === "ask" ? api.askAboutLink(cardId, link.id) : api.deleteCardLink(cardId, link.id),
  );
  const update = useCardChange(cardId, (values: CardLinkInput) =>
    api.updateCardLink(cardId, link.id, values),
  );
  const isAdmin = hasPermission(me.permissions, "research.approve");
  const canChange = mayChange(me, link.addedBy.id);
  const editable =
    canChange &&
    (link.status === "draft" || link.status === "declined" || (isAdmin && link.status === "approved"));
  const canAsk = canChange && (link.status === "draft" || link.status === "declined");
  const run = (step: "ask" | "delete", done: string) =>
    change.mutate(step, {
      onSuccess: () => toast.success(done),
      onError: (e) => toast.error(describeError(e).message),
    });

  return (
    <TableRow className="align-top">
      <TableCell className="max-w-72 whitespace-normal">
        {link.url === null ? (
          <span className="font-medium text-foreground">{link.website}</span>
        ) : (
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium text-foreground hover:underline"
          >
            {link.website}
            <ExternalLink className="size-3.5 shrink-0" aria-hidden="true" />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        )}
        <p className="truncate text-xs text-muted-foreground" title={link.url ?? undefined}>
          {describeSource(link)}
        </p>
        {link.notes !== null && <p className="mt-1 text-xs text-foreground">“{link.notes}”</p>}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1.5 font-semibold">
          {describeLinkPrice(link)}
          <PriorityBadge priority={link.priority} />
        </div>
      </TableCell>
      <TableCell className="whitespace-normal text-muted-foreground">{describeLinkCondition(link)}</TableCell>
      <TableCell className="whitespace-normal">
        <Badge tone={LINK_STATUS_TONES[link.status]}>{LINK_STATUS_LABELS[link.status]}</Badge>
        {link.decision !== null && link.status !== "waiting" && (
          <p className="mt-1 max-w-56 text-xs text-muted-foreground">
            {link.decision.outcome === "approved"
              ? `Approved${link.decision.approvedAmount === null ? "" : ` at ${formatMoney({ amount: link.decision.approvedAmount, currency: link.currency })}`} by ${link.decision.decidedBy.name}`
              : `Declined by ${link.decision.decidedBy.name}`}
            {link.decision.note !== null && <> · “{link.decision.note}”</>}
          </p>
        )}
      </TableCell>
      <TableCell>
        <DealControls link={link} me={me} />
      </TableCell>
      <TableCell>{link.addedBy.id === me.user.id ? "You" : link.addedBy.name}</TableCell>
      <TableCell>
        <div className="flex flex-wrap justify-end gap-1.5">
          {canAsk && (
            <Button
              size="sm"
              disabled={change.isPending}
              onClick={() => {
                // Opened straight from the click, so the browser does not block it; the request is saved alongside.
                window.open(
                  whatsAppAskLink(cardName, link, me.user.name, `${window.location.origin}/cards/${cardId}`),
                  "_blank",
                  "noopener,noreferrer",
                );
                run("ask", "Saved as waiting. Press Send in WhatsApp to tell the admin.");
              }}
            >
              <Send aria-hidden="true" />
              {link.status === "declined" ? "Ask admin again on WhatsApp" : "Ask admin on WhatsApp"}
            </Button>
          )}
          {isAdmin && link.status === "waiting" && (
            <>
              <Button size="sm" onClick={() => setDeciding("approve")}>
                <Check aria-hidden="true" />
                Approve…
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setDeciding("decline")}>
                <X aria-hidden="true" />
                Decline…
              </Button>
            </>
          )}
          {editable && (
            <Button
              size="icon"
              variant="ghost"
              className="size-8"
              aria-label="Edit"
              title="Edit"
              onClick={() => setEditing(true)}
            >
              <Pencil aria-hidden="true" />
            </Button>
          )}
          {canChange && (isAdmin || link.status === "draft" || link.status === "declined") && (
            <Button
              size="icon"
              variant="ghost"
              className="size-8"
              aria-label="Delete"
              title="Delete"
              onClick={() => setRemoving(true)}
            >
              <Trash2 aria-hidden="true" />
            </Button>
          )}
        </div>
      </TableCell>
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit listing</DialogTitle>
            <DialogDescription>{link.website}</DialogDescription>
          </DialogHeader>
          <LinkForm
            link={link}
            onCancel={() => setEditing(false)}
            onSave={async (values) => {
              await update.mutateAsync(values);
              setEditing(false);
              toast.success("Listing updated.");
            }}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        title="Delete this listing?"
        description={link.url ?? link.website}
        confirmText="Delete"
        destructive
        onConfirm={() => {
          setRemoving(false);
          run("delete", "Listing deleted.");
        }}
      />

      {deciding !== null && (
        <DecisionDialog cardId={cardId} link={link} kind={deciding} onDone={() => setDeciding(null)} />
      )}
    </TableRow>
  );
}

/** The admin's answer: approve (the amount can be changed) or decline with a reason. */
function DecisionDialog({
  cardId,
  link,
  kind,
  onDone,
}: {
  cardId: string;
  link: CardLink;
  kind: "approve" | "decline";
  onDone: () => void;
}) {
  const api = useApi();
  const [amount, setAmount] = useState(link.price ?? "");
  const [text, setText] = useState("");
  const amountId = useId();
  const textId = useId();
  const decide = useCardChange(cardId, () =>
    kind === "approve"
      ? api.approveLink(cardId, link.id, { approvedAmount: amount, note: text })
      : api.declineLink(cardId, link.id, { reason: text }),
  );
  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    decide.mutate(undefined, {
      onSuccess: () => {
        toast.success(
          kind === "approve" ? "Approved. The employee is notified." : "Declined. The employee is notified.",
        );
        onDone();
      },
    });
  };
  const err = (path: string) => (decide.isError ? serverFieldError(decide.error, path) : undefined);

  return (
    <Dialog open onOpenChange={(open) => !open && onDone()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{kind === "approve" ? "Approve listing" : "Decline listing"}</DialogTitle>
            <DialogDescription>
              {link.website}: {describeLinkPrice(link)} · {describeLinkCondition(link)}
            </DialogDescription>
          </DialogHeader>
          {kind === "approve" && (
            <Field id={amountId} label={`Approved amount (${link.currency})`} error={err("approvedAmount")}>
              <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
            </Field>
          )}
          <Field
            id={textId}
            label={kind === "approve" ? "Note (optional)" : "Reason for declining"}
            error={err(kind === "approve" ? "note" : "reason")}
          >
            <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} />
          </Field>
          {decide.isError && err("approvedAmount") === undefined && err("reason") === undefined && (
            <ErrorAlert error={decide.error} />
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onDone} disabled={decide.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant={kind === "approve" ? "primary" : "danger"}
              disabled={decide.isPending}
            >
              {kind === "approve" ? "Approve" : "Decline"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
